import { Alpha3Code } from 'i18n-iso-countries'
import { useMemo } from 'react'

import { GroupedMultiSelectOptions } from '@percent/lemonade'
import { useQuery } from '@percent/partner-dashboard/common/hooks'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import {
  PartnerConfigData,
  PartnerConfigKey,
  PartnerRegionsConfigValue
} from '@percent/partner-dashboard/context/config/PartnerConfigProvider.types'

type UseRegionCountryOptionsParams = {
  fallbackGroupLabel: string
}

const hasRegions = (value: PartnerConfigData['value'] | undefined): value is PartnerRegionsConfigValue =>
  !!value && !Array.isArray(value) && Object.keys(value).length > 0

export function useRegionCountryOptions({ fallbackGroupLabel }: UseRegionCountryOptionsParams) {
  const { partnerService } = useServices()
  const { countries, getCountryNameByCode } = useCountries()
  const [{ data: configData, isLoading }] = useQuery(partnerService.getConfig, PartnerConfigKey.REGIONS)

  const options = useMemo<GroupedMultiSelectOptions>(() => {
    const regions = configData?.value
    const labelFor = (code: string) => getCountryNameByCode(code as Alpha3Code) || code

    if (hasRegions(regions)) {
      return Object.fromEntries(
        Object.values(regions).map(region => [
          region.name,
          region.countryCodes.map(code => ({ value: code, label: labelFor(code) }))
        ])
      )
    }

    return {
      [fallbackGroupLabel]: (countries ?? []).map(country => ({ value: country.code, label: country.name }))
    }
  }, [configData, countries, getCountryNameByCode, fallbackGroupLabel])

  // Deliberately only the config query: useCountries cannot report failure, so folding `!countries`
  // in here would leave callers that gate on isLoading stuck on a loader forever if that request
  // fails. Until countries arrive, labelFor falls back to the country code.
  return { options, isLoading }
}
